import { useMemo, useState, type FormEvent } from 'react';
import type { Framework, SubmitCertificateForVerificationRequest, VendorDeviceRef } from '@mvcp/contracts';
import { submitCertificateForVerification } from '../api';
import { MAX_TEXT_LEN } from '../lib/field-limits';
import { deviceLabel } from './device-label';
import { proposedOptionSuffix } from './proposed-tag';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/**
 * Compliance-Manager "Add certificate" form (Story 6.10, FR-11). REPLACES the retired
 * Story 6.8 local-create: a manager no longer writes a private backbone certificate.
 * This SUBMITS the certificate for System-Administrator verification — the manager-facing
 * twin of the vendor {@link DeclareCertificateForm}, including its REQUIRED evidence file
 * — and the submission lands in staging as "pending verification". On admin approval it
 * is promoted to the trusted backbone and propagates to every company that shares the
 * subject ("certify once"). It is NEVER presented here as verified/trusted.
 *
 * Same scope→subject coherence as the vendor form: a company-scoped framework targets the
 * Vendor, a device-scoped one targets a Device. The subject is DERIVED from the cockpit
 * context (`vendorId` + the in-scope `devices`), never free-typed. On success `onCreated()`
 * fires so the parent re-reads the subject's certificate + pending-submission lists.
 */
export function CreateCertificateForm({
  frameworks,
  vendorId,
  devices = [],
  onCreated,
  onCancel,
}: {
  /** Applicable frameworks for this subject (`listApplicableFrameworks`). */
  frameworks: Framework[];
  /** The subject Vendor id (company-scoped subject; also the device's manufacturer). */
  vendorId: string;
  /** Device-subject options for a device-scoped framework (id + model label). */
  devices?: VendorDeviceRef[];
  onCreated: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  // A single device (the Device-detail case) is unambiguous → pre-select it so the
  // manager need not re-pick the record they are already on.
  const defaultDeviceId = devices.length === 1 ? devices[0]!.id : '';
  const [frameworkId, setFrameworkId] = useState('');
  const [deviceId, setDeviceId] = useState(defaultDeviceId);
  const [approvingAuthority, setApprovingAuthority] = useState('');
  const [expiresAt, setExpiresAt] = useState(''); // an <input type="date"> value (YYYY-MM-DD)
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(
    () => frameworks.find((f) => f.id === frameworkId),
    [frameworks, frameworkId],
  );
  const isDeviceScoped = selected?.scope === 'device';

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!selected) {
      setError('Select a framework.');
      return;
    }
    const subjectType: 'vendor' | 'device' = isDeviceScoped ? 'device' : 'vendor';
    if (subjectType === 'device' && !deviceId) {
      setError('Select the device this certificate applies to.');
      return;
    }
    if (!file) {
      setError('Attach evidence (PDF, PNG, or JPEG). Every submission needs evidence.');
      return;
    }

    const request: SubmitCertificateForVerificationRequest = {
      frameworkId: selected.id,
      subjectType,
      vendorId,
      ...(subjectType === 'device' ? { deviceId } : {}),
      ...(approvingAuthority.trim() ? { approvingAuthority: approvingAuthority.trim() } : {}),
      // A date input yields YYYY-MM-DD; the API wants a full ISO timestamp WITH a tz
      // offset, so pin it to UTC midnight (mirrors DeclareCertificateForm).
      ...(expiresAt ? { expiresAt: `${expiresAt}T00:00:00.000Z` } : {}),
    };

    setSubmitting(true);
    try {
      await submitCertificateForVerification(request, file);
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't submit the certificate. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="stack"
      aria-label="Submit a certificate for verification"
      style={{ gap: 10 }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
        <label className="stack" style={{ gap: 4, flex: '1 1 240px' }}>
          <span className="field-label">Framework</span>
          <select
            aria-label="Framework"
            value={frameworkId}
            onChange={(e) => {
              setFrameworkId(e.target.value);
              setDeviceId(defaultDeviceId);
              setError(null);
            }}
            style={fieldStyle}
          >
            <option value="">Select a framework…</option>
            {frameworks.map((f) => (
              <option key={f.id} value={f.id}>
                {f.code} — {f.name}
              </option>
            ))}
          </select>
        </label>

        {isDeviceScoped && (
          <label className="stack" style={{ gap: 4, flex: '1 1 200px' }}>
            <span className="field-label">Device</span>
            <select
              aria-label="Device"
              value={deviceId}
              onChange={(e) => {
                setDeviceId(e.target.value);
                setError(null);
              }}
              style={fieldStyle}
            >
              <option value="">Select a device…</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {/* Story 8.5 (AD-25): a device SUBJECT is model + revision, composed by
                      the one web owner. Two revisions of one model are now
                      distinguishable at the point coverage is asserted; a version-less
                      device renders the bare model with no stray artifact.

                      Story 8.9: a device that is still a tenant PROPOSAL says so, in the
                      marker owner's text form — an `<option>` cannot host the pill, and
                      the status may not enter `deviceLabel` (AD-25 clause 3). A curated
                      device renders exactly the text it did before, with no trailing
                      separator. */}
                  {deviceLabel(d)}
                  {proposedOptionSuffix(d.proposed)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="stack" style={{ gap: 4, flex: '1 1 180px' }}>
          <span className="field-label">Approving authority</span>
          <input
            aria-label="Approving authority"
            value={approvingAuthority}
            onChange={(e) => setApprovingAuthority(e.target.value)}
            maxLength={MAX_TEXT_LEN}
            style={fieldStyle}
          />
        </label>

        <label className="stack" style={{ gap: 4, flex: '0 1 160px' }}>
          <span className="field-label">Expires</span>
          <input
            type="date"
            aria-label="Expiry date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            style={fieldStyle}
          />
        </label>

        <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
          <span className="field-label">Evidence</span>
          <input
            type="file"
            aria-label="Evidence file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
            style={fieldStyle}
          />
        </label>

        <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit for verification'}
        </button>
        <button type="button" className="icon-button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        Submitted certificates are reviewed by the platform administrator. Once verified,
        they become visible to every company that shares this vendor or device.
      </p>
      <div role="alert" aria-live="assertive" style={{ minHeight: 16 }}>
        {error && (
          <span className="status-pill" data-tone="bad">
            <span className="status-pill__dot" aria-hidden="true" />
            {error}
          </span>
        )}
      </div>
    </form>
  );
}
